import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type CSSProperties,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

/**
 * Section 10's virtualization work (`improvements/perf-baselines/
 * 10-virtualization-benchmark.md`), generalized off the first template
 * (`basic/home/magazine/magazine.tsx`) to the rest of the issue-grid
 * templates (`poets`/`padi`/`sections`/`morningstar`).
 *
 * **Every affected template's real scroll container turned out to be the
 * same architectural pattern, not `window`.** Every template's app shell
 * (`home.module.css`'s `.view { grid-template-rows: topbar 1fr navbar }`
 * plus `.view .content { overflow: auto }`) makes `.content` -- not the
 * page/`window` -- the thing that actually scrolls, at every breakpoint.
 * The first version of this work assumed `basic`'s mobile grid was
 * page-scrolling and used `useWindowVirtualizer`; that was wrong (caught
 * before shipping further, not after) -- it happened to *look* right in
 * an isolated Storybook story specifically because the story had no
 * `.content` ancestor constraining/scrolling it, so the browser window
 * really was the only scrollable thing in that one isolated case. Inside
 * the real app shell it wasn't, and would have silently frozen the visible
 * rows in place while `.content` scrolled underneath it.
 *
 * Fixed by not assuming *which* element scrolls at all: `useRowVirtualizer`
 * walks up from wherever it's mounted to find the real scrollable
 * ancestor via computed `overflow-y` (`auto`/`scroll`), the same technique
 * general-purpose virtualization libraries use for "auto-detect scroll
 * parent." Falls back to `document.scrollingElement` if nothing qualifies
 * (a template that turns out to be genuine page-scroll, if one exists) --
 * `@tanstack/react-virtual`'s `useVirtualizer` accepts that directly, no
 * separate `useWindowVirtualizer` needed.
 *
 * What's still template-specific and not attempted here: the column count
 * per breakpoint (`basic`/`sections` use 2 tablet columns, `poets`/`padi`/
 * `morningstar` use 3), whether there's a "feature" first row (see
 * `RowChunkOptions`), and whether virtualizing above the desktop-panel
 * breakpoint is worth it at all (every template examined so far treats
 * that as a second, separately-laid-out panel and this work leaves it
 * unvirtualized -- see each template file's own comment for why).
 */

/** Matches every affected template's own tablet breakpoint (`min-width: 768px`). */
export const TABLET_BREAKPOINT = 768;
/**
 * Matches every affected template's own desktop breakpoint, where the grid
 * either stops being part of `.content`'s single-column flow and becomes a
 * second, differently-laid-out panel (`basic`/`poets`/`padi`/`morningstar`),
 * or just changes column count again (`sections`) -- see each template's
 * own comment for which, and why this pass doesn't virtualize past it.
 */
export const DESKTOP_PANEL_BREAKPOINT = 1024;

/**
 * `window.innerWidth` + a `resize` listener, not `useMediaQuery`
 * (`matchMedia`-based): this codebase's Vitest setup globally stubs
 * `window.matchMedia` to always report `matches: false` (`src/test/
 * setup.ts`, needed since jsdom has no real implementation and several
 * modules call it at import time) -- fine for code that only ever checks
 * one fixed thing, but it makes `matchMedia`-based breakpoint state
 * untestable for *either* side of a boundary, always resolving to the
 * "below every breakpoint" branch. `window.innerWidth` doesn't have that
 * problem and is exactly as cheap. No PWA installed on a phone actually
 * crosses a breakpoint mid-session, but the `resize` listener is a cheap
 * correctness fallback (orientation change, a dev resizing a window)
 * rather than the reason this works.
 */
export function useResponsiveGridBreakpoint() {
	const [width, setWidth] = useState(() =>
		typeof window === 'undefined' ? 0 : window.innerWidth,
	);
	useEffect(() => {
		const onResize = () => setWidth(window.innerWidth);
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);
	return {
		width,
		/** `768px <= width < 1024px`. */
		isTablet: width >= TABLET_BREAKPOINT && width < DESKTOP_PANEL_BREAKPOINT,
		isDesktopPanel: width >= DESKTOP_PANEL_BREAKPOINT,
	};
}

/**
 * Walks up from `start` looking for the element that actually scrolls
 * (computed `overflow-y: auto` or `scroll`), stopping at -- and including
 * -- `document.body`/`document.documentElement` so a genuinely
 * page-scrolling layout still resolves to something. Not a general DOM
 * utility placed here on purpose: it's a small, virtualization-specific
 * tool, not a shared concern the rest of the app has needed before this.
 */
function findScrollableAncestor(start: Element | null): Element {
	let node = start?.parentElement ?? null;
	while (node) {
		const overflowY = window.getComputedStyle(node).overflowY;
		if (overflowY === 'auto' || overflowY === 'scroll') {
			return node;
		}
		if (node === document.documentElement) break;
		node = node.parentElement;
	}
	return document.scrollingElement ?? document.documentElement;
}

export interface RowChunkOptions {
	itemCount: number;
	columns: number;
	/**
	 * When true, item 0 is alone in row 0 (a single item spanning the full
	 * row width -- `poets`/`padi`/`morningstar`'s tablet "feature" grid
	 * area); every row after that is a `columns`-sized chunk of the
	 * remaining items. When false (the common case), every row including
	 * the first is a uniform `columns`-sized chunk.
	 */
	featureFirstRow?: boolean;
}

export function computeRowCount({
	itemCount,
	columns,
	featureFirstRow,
}: RowChunkOptions): number {
	if (itemCount <= 0) return 0;
	if (featureFirstRow) {
		return 1 + Math.ceil(Math.max(itemCount - 1, 0) / columns);
	}
	return Math.ceil(itemCount / columns);
}

export function rowItemIndices(
	rowIndex: number,
	{ itemCount, columns, featureFirstRow }: RowChunkOptions,
): Array<number> {
	if (featureFirstRow) {
		if (rowIndex === 0) {
			return itemCount > 0 ? [0] : [];
		}
		const start = 1 + (rowIndex - 1) * columns;
		return Array.from({ length: columns }, (_, i) => start + i).filter(
			(i) => i < itemCount,
		);
	}
	const start = rowIndex * columns;
	return Array.from({ length: columns }, (_, i) => start + i).filter(
		(i) => i < itemCount,
	);
}

export interface VirtualGridRow {
	key: React.Key;
	itemIndices: Array<number>;
	style: CSSProperties;
	/**
	 * `measureElement` (`@tanstack/virtual-core`'s `getElementSize`) reads
	 * this attribute off the measured DOM node to know which virtual row it
	 * belongs to -- spread `rowProps` onto the row element (not just
	 * `style`) or dynamic remeasurement silently can't correlate
	 * measurements back to rows (a dev-only console warning, easy to miss:
	 * "Missing attribute name 'data-index={index}' on measured element").
	 */
	rowProps: { 'data-index': number };
}

export interface UseRowVirtualizerResult {
	/** Attach to the element that wraps the virtualized rows -- also the
	 * starting point for the scrollable-ancestor search above. */
	containerRef: React.RefObject<HTMLDivElement | null>;
	totalSize: number;
	rows: Array<VirtualGridRow>;
	measureElement: (node: Element | null) => void;
}

interface UseRowVirtualizerOptions {
	/**
	 * A starting guess for one row's rendered height -- corrected against
	 * the real rendered height per row by the virtualizer's own dynamic
	 * measurement (via this hook's returned `measureElement`), so this
	 * doesn't need to be exact. Must be a stable value across renders (a
	 * component-level constant, not something recomputed inline).
	 */
	estimateRowHeight: number;
	overscan?: number;
}

export function useRowVirtualizer(
	chunkOptions: RowChunkOptions,
	{ estimateRowHeight, overscan = 4 }: UseRowVirtualizerOptions,
): UseRowVirtualizerResult {
	const containerRef = useRef<HTMLDivElement>(null);
	const [scrollElement, setScrollElement] = useState<Element | null>(null);

	useLayoutEffect(() => {
		setScrollElement(findScrollableAncestor(containerRef.current));
	}, []);

	// Referentially stable across renders on purpose -- a fresh inline
	// function every render reads as "options changed" to the virtualizer,
	// which re-triggers its internal render-triggering state update. Left
	// unmemoized this produced a genuine infinite render loop (`Maximum
	// update depth exceeded`) once a grid was big enough for the
	// remeasure-then-rerender cycle to keep tripping React's safety check.
	const estimateSize = useCallback(() => estimateRowHeight, [estimateRowHeight]);
	const getScrollElement = useCallback(() => scrollElement, [scrollElement]);

	const virtualizer = useVirtualizer({
		count: computeRowCount(chunkOptions),
		getScrollElement,
		estimateSize,
		overscan,
	});

	const rows: Array<VirtualGridRow> = virtualizer.getVirtualItems().map(
		(virtualRow) => ({
			key: virtualRow.key,
			itemIndices: rowItemIndices(virtualRow.index, chunkOptions),
			style: {
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				transform: `translateY(${virtualRow.start}px)`,
			},
			rowProps: { 'data-index': virtualRow.index },
		}),
	);

	return {
		containerRef,
		totalSize: virtualizer.getTotalSize(),
		rows,
		measureElement: virtualizer.measureElement,
	};
}
